import type mapboxgl from "mapbox-gl";

import { DEFAULT_SHAPEFILE_THICKNESS } from "components/project/shapefiles/shapefiles.constants";

type NumberOrExpression = number | mapboxgl.Expression;

const RADIUS_ZOOM_BASE = 1.8;

// The radius a dot gets at DEFAULT_SHAPEFILE_THICKNESS; other thicknesses scale from here.
const RADIUS_BY_ZOOM_AT_REFERENCE: ReadonlyArray<readonly [number, number]> = [
  [6, 2],
  [15, 25],
  [22, 30]
];

// Both guard persisted data only: the slider never sends 0, and its max of 10 stays under the cap.
const MIN_POINT_RADIUS_PX = 1;
const MAX_POINT_RADIUS_PX = 70;

function clampRadius(radius: NumberOrExpression): NumberOrExpression {
  if (typeof radius === "number") {
    return Math.min(Math.max(radius, MIN_POINT_RADIUS_PX), MAX_POINT_RADIUS_PX);
  }
  return [
    "max",
    ["min", radius, MAX_POINT_RADIUS_PX],
    MIN_POINT_RADIUS_PX
  ] as mapboxgl.Expression;
}

// Dot area, not radius, tracks thickness so each slider step reads as a similar increase.
function scaleRadius(
  radiusAtReference: number,
  thickness: NumberOrExpression
): NumberOrExpression {
  if (typeof thickness === "number") {
    // Same recovery as parseThickness on the feature-state path: a bad value means the default dot.
    if (!Number.isFinite(thickness)) return clampRadius(radiusAtReference);
    return clampRadius(
      radiusAtReference * Math.sqrt(Math.max(thickness, 0) / DEFAULT_SHAPEFILE_THICKNESS)
    );
  }
  return clampRadius([
    "*",
    radiusAtReference,
    ["sqrt", ["/", ["max", thickness, 0], DEFAULT_SHAPEFILE_THICKNESS]]
  ] as mapboxgl.Expression);
}

/**
 * Zoom-interpolated circle-radius for shapefile point layers. The default
 * thickness reproduces the original 2/25/30px ramp; other thicknesses scale
 * the dot's area, clamped so a thin setting never erases the dot and a thick
 * one never balloons it.
 */
export function buildShapefilePointRadius(
  thickness: NumberOrExpression
): mapboxgl.Expression {
  const stops = RADIUS_BY_ZOOM_AT_REFERENCE.flatMap(([zoom, radiusAtReference]) => [
    zoom,
    scaleRadius(radiusAtReference, thickness)
  ]);
  return [
    "interpolate",
    ["exponential", RADIUS_ZOOM_BASE],
    ["zoom"],
    ...stops
  ] as mapboxgl.Expression;
}
